import { useEffect, useMemo, useState } from "react";
import { list, create, update, remove, formatApiError, uploadFile, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtIDR, fmtDate } from "@/lib/constants";

export function FileField({ value, filename, name, onUploaded, onClear, accept }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const res = await uploadFile(file); onUploaded(res); toast.success("File terunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal upload"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-2 bg-beige/60 rounded-lg px-3 py-2">
          <FileText className="w-4 h-4 text-burgundy shrink-0" />
          <a href={fileUrl(value)} target="_blank" rel="noreferrer" className="text-sm text-burgundy underline flex-1 truncate" data-testid={`file-link-${name}`}>{filename || "Lihat file"}</a>
          <button type="button" onClick={onClear} className="text-[#7A7A7A] hover:text-red-600" data-testid={`file-clear-${name}`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <label className="flex items-center gap-2 border border-dashed border-gold/40 rounded-lg px-3 py-2 cursor-pointer hover:bg-beige/40 text-sm text-[#7A7A7A]" data-testid={`file-input-${name}`}>
          <Upload className="w-4 h-4" />
          {busy ? "Mengunggah..." : "Pilih file"}
          <input type="file" accept={accept} className="hidden" onChange={handle} disabled={busy} />
        </label>
      )}
    </div>
  );
}

export function useResource(resource, params = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);
  const reload = () => {
    setLoading(true);
    return list(resource, params).then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [resource, key]);
  return { items, loading, reload, setItems };
}

export function ResourceForm({ open, onOpenChange, fields, initial, resource, onSaved, title, defaults = {} }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const editing = initial && initial.id;

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => { base[f.name] = initial?.[f.name] ?? f.default ?? (f.type === "number" ? 0 : (f.type === "boolean" ? false : "")); });
      setForm({ ...base, ...(!editing ? defaults : {}) });
    }
  }, [open]); // eslint-disable-line

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (form[f.name] === "" || form[f.name] == null)) {
        toast.error(`${f.label} wajib diisi`); return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0); });
      const merged = { ...defaults, ...payload };
      if (editing) await update(resource, initial.id, merged);
      else await create(resource, merged);
      toast.success(editing ? "Data diperbarui" : "Data ditambahkan");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-burgundy">{title || (editing ? "Edit" : "Tambah")}</DialogTitle>
          <DialogDescription className="sr-only">Formulir data</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-semibold text-[#4A4A4A] mb-1.5 block">{f.label}{f.required && <span className="text-burgundy"> *</span>}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} rows={3} />
              ) : f.type === "select" ? (
                <Select value={(String(form[f.name] ?? "") === "" ? "__none__" : String(form[f.name]))} onValueChange={(v) => set(f.name, v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Pilih..." /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => { const val = String(o.value ?? o); return <SelectItem key={val || "__none__"} value={val === "" ? "__none__" : val}>{o.label ?? o}</SelectItem>; })}</SelectContent>
                </Select>
              ) : f.type === "boolean" ? (
                <Select value={String(form[f.name] ? "true" : "false")} onValueChange={(v) => set(f.name, v === "true")}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Ya</SelectItem><SelectItem value="false">Tidak</SelectItem></SelectContent>
                </Select>
              ) : f.type === "file" ? (
                <FileField value={form[f.name]} filename={form[f.name + "_filename"]} name={f.name}
                  onUploaded={(res) => { set(f.name, res.storage_path); set(f.name + "_filename", res.original_filename); }}
                  onClear={() => { set(f.name, ""); set(f.name + "_filename", ""); }} />
              ) : (
                <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-submit" className="bg-burgundy hover:bg-burgundy-dark text-white">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function renderCell(col, row) {
  const v = row[col.key];
  if (col.render) return col.render(row);
  if (col.badge) return <StatusBadge status={v} />;
  if (col.money) return fmtIDR(v);
  if (col.date) return fmtDate(v);
  if (col.bool) return v ? "Ya" : "Tidak";
  return v ?? "-";
}

export default function ResourcePage({ resource, title, subtitle, overline, columns, fields, defaults = {}, writeLevel = 2, filterKey, params = {}, PageHeaderComp, rowActions }) {
  const { can } = useAuth();
  const { items, loading, reload } = useResource(resource, params);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((i) => Object.values(i).some((v) => typeof v === "string" && v.toLowerCase().includes(s)));
  }, [items, q]);

  const doDelete = async () => {
    try { await remove(resource, delItem.id); toast.success("Data dihapus"); setDelItem(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editable = can(writeLevel);

  return (
    <div>
      {PageHeaderComp}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A7A]" />
          <Input data-testid={`${resource}-search`} placeholder="Cari..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full bg-white" />
        </div>
        {editable && (
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid={`${resource}-add-btn`} className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        )}
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                {columns.map((c) => <TableHead key={c.key} className="text-[11px] uppercase tracking-wider text-[#4A4A4A] font-semibold">{c.label}</TableHead>)}
                {editable && <TableHead className="text-right text-[11px] uppercase tracking-wider text-[#4A4A4A] font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-[#7A7A7A]">Memuat...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-[#7A7A7A]">Belum ada data.</TableCell></TableRow>
              ) : filtered.map((row) => (
                <TableRow key={row.id} className="border-gold/10 hover:bg-beige/30" data-testid={`${resource}-row`}>
                  {columns.map((c) => <TableCell key={c.key} className="text-sm text-[#4A4A4A]">{renderCell(c, row)}</TableCell>)}
                  {editable && (
                    <TableCell className="text-right whitespace-nowrap">
                      {rowActions && rowActions(row)}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-[#7A7A7A] hover:text-burgundy" onClick={() => { setEditing(row); setFormOpen(true); }} data-testid={`${resource}-edit-btn`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-[#7A7A7A] hover:text-red-600" onClick={() => setDelItem(row)} data-testid={`${resource}-delete-btn`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={editing} resource={resource} defaults={defaults} onSaved={reload} title={editing ? `Edit ${title}` : `Tambah ${title}`} />

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus data?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-confirm" className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
